import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import API_BASE from "./config";

// Base URL of the nfviz server that serves the designer applet (`/designer.html`).
// It is reverse-proxied under the app's own origin, so the iframe is SAME-ORIGIN —
// no CSP/frameAncestors handling is needed (same reason the viewer embed works).
// Falls back to the viewer's base when a dedicated designer base isn't configured.
const DESIGNER_BASE =
  process.env.REACT_APP_DESIGNER_BASE || process.env.REACT_APP_NFVIZ_BASE;

const WorkflowDesigner = ({ userData, onClose }) => {
  const iframeRef = useRef(null);

  // Finalize status shown as a banner: null | { status, ... }.
  //   status: "validating" | "success" | "error"
  const [finalize, setFinalize] = useState(null);

  // Each design writes into its own folder inside the server's outputRoot. Fix it once
  // per open (timestamped) so re-renders don't retarget mid-session and repeat opens
  // don't overwrite each other's main.nf.
  const folderRef = useRef(
    `design-${userData?.cognitoUserId || userData?.userId || "user"}-${Date.now()}`
  );

  const src =
    `${DESIGNER_BASE}/designer.html` +
    `?folder=${encodeURIComponent(folderRef.current)}&theme=light&grid=on`;

  // Save -> run our finalize pipeline (Claude validation -> stub-run -> persist).
  const finalizeDesign = async (folder) => {
    if (!folder) return;
    setFinalize({ status: "validating" });
    try {
      const res = await axios.post(
        `${API_BASE}/analysis/designer-finalize`,
        { folder },
        { withCredentials: true }
      );
      const d = res.data || {};
      if (d.ok) {
        setFinalize({ status: "success", shortName: d.shortName, summary: d.summary, corrected: d.corrected });
      } else if (d.stage === "validation") {
        setFinalize({ status: "error", title: "Validation failed", detail: d.issues || d.message });
      } else if (d.stage === "stub_run") {
        setFinalize({ status: "error", title: "Stub-run failed", detail: d.log || "The workflow did not compile in a stub run." });
      } else {
        setFinalize({ status: "error", title: "Could not save", detail: d.message || d.error || "Unknown error." });
      }
    } catch (err) {
      setFinalize({
        status: "error",
        title: "Could not save",
        detail: err.response?.data?.error || err.message || "Request failed.",
      });
    }
  };

  // React to the applet's postMessage events (save / validation / errors).
  useEffect(() => {
    const onMessage = (event) => {
      const m = event.data;
      if (!m || typeof m !== "object") return;
      switch (m.type) {
        case "nfviz:saved":
          // { folder, files, name, warnings } — the design was written to outputRoot.
          finalizeDesign(m.folder || folderRef.current);
          break;
        case "nfviz:invalid":
          setFinalize({ status: "error", title: "Design incomplete", detail: (m.issues || [m.reason]).join?.(" ") || m.reason || "Fix the highlighted issues and save again." });
          break;
        case "nfviz:error":
          setFinalize({ status: "error", title: "Designer error", detail: m.message || "" });
          break;
        default:
          break;
      }
    };
    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div style={{ flex: 1, display: "flex", flexDirection: "column", minHeight: 0 }}>
      <div
        style={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
          marginBottom: "12px",
        }}
      >
        <h1 style={{ color: "#0f172a", margin: 0 }}>Workflow Designer</h1>
        <div style={{ display: "flex", gap: "8px" }}>
          <a
            href="/designer-tutorial"
            target="_blank"
            rel="noopener noreferrer"
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background: "linear-gradient(135deg, #8b5cf6, #7c3aed)",
              color: "#fff",
              cursor: "pointer",
              textDecoration: "none",
              display: "inline-flex",
              alignItems: "center",
            }}
          >
            📘 Tutorial
          </a>
          <button
            onClick={onClose}
            style={{
              padding: "8px 16px",
              borderRadius: "10px",
              border: "none",
              background: "#e2e8f0",
              color: "#0f172a",
              cursor: "pointer",
            }}
          >
            ✕ Close
          </button>
        </div>
      </div>
      {finalize && (
        <div
          style={{
            marginBottom: "12px",
            padding: "12px 16px",
            borderRadius: "10px",
            display: "flex",
            alignItems: "flex-start",
            justifyContent: "space-between",
            gap: "12px",
            background:
              finalize.status === "success" ? "#ecfdf5"
              : finalize.status === "error" ? "#fef2f2"
              : "#eff6ff",
            border:
              "1px solid " +
              (finalize.status === "success" ? "#6ee7b7"
              : finalize.status === "error" ? "#fca5a5"
              : "#bfdbfe"),
            color:
              finalize.status === "success" ? "#065f46"
              : finalize.status === "error" ? "#7f1d1d"
              : "#1e40af",
          }}
        >
          <div style={{ minWidth: 0 }}>
            {finalize.status === "validating" && (
              <span>⏳ Validating workflow and running a stub check… please wait.</span>
            )}
            {finalize.status === "success" && (
              <span>
                ✅ <strong>Saved.</strong> Validated &amp; stub-checked as{" "}
                <strong>{finalize.shortName}</strong>.
                {finalize.corrected ? " (auto-corrected during validation)" : ""}
                {finalize.summary ? <div style={{ marginTop: 4, fontSize: 13 }}>{finalize.summary}</div> : null}
              </span>
            )}
            {finalize.status === "error" && (
              <span>
                ❌ <strong>{finalize.title || "Failed"}.</strong>
                {finalize.detail ? (
                  <pre style={{ whiteSpace: "pre-wrap", margin: "6px 0 0", fontSize: 12, maxHeight: 160, overflowY: "auto" }}>
                    {finalize.detail}
                  </pre>
                ) : null}
              </span>
            )}
          </div>
          {finalize.status !== "validating" && (
            <button
              onClick={() => setFinalize(null)}
              style={{ border: "none", background: "transparent", cursor: "pointer", color: "inherit", fontWeight: "bold" }}
            >
              ✕
            </button>
          )}
        </div>
      )}
      <iframe
        ref={iframeRef}
        title="Workflow Designer"
        src={src}
        style={{
          flex: 1,
          width: "100%",
          border: "none",
          borderRadius: "16px",
          background: "#fff",
          boxShadow: "0 20px 40px rgba(0,0,0,0.08)",
        }}
      />
    </div>
  );
};

export default WorkflowDesigner;
